/* @flow */

import React from 'react';
import Relay from 'react-relay';
import Helmet from 'react-helmet';
import { css, media } from 'glamor';

import { withRouter } from 'depop/contextWrappers';

import { FAMILY_STANDARD_BOLD } from 'depop/styles/typography';

import { FormattedMessage, injectIntl } from 'react-intl';

import { formatProductPrice } from 'depop/utils/money';

import Application from 'depop/root/containers/Application';

import isProductViewable from 'depop/utils/isProductViewable';

import { ScrollTop } from 'depop/components/scroll';

import NotFound from 'depop/root/views/NotFound';
import Picture from 'depop/components/images/Picture';
import VideoPlayer from 'depop/components/videos/VideoPlayer';
import Carousel from 'depop/components/images/Carousel';

import { UserSummary } from 'depop/components/UserSummary';
import DeeplinkConfig from 'depop/components/deeplinks/DeeplinkConfig';
import SimilarProducts from '../components/SimilarProducts';
import LikeButton from '../components/LikeButton';
import BuyButton from '../components/BuyButton';
import DeeplinkBuyButton from '../components/DeeplinkBuyButton';
import ProductComments from '../components/ProductComments';

import UserText from 'depop/components/UserText';

import ProductDetailHead from './_ProductDetailHead';

class ProductDetail extends React.Component {
  props: {
    viewer: Object,
    intl: Object,
    location: Object,
    router: Object,
  };

  componentDidMount() {
    const { router, location, viewer: { product } } = this.props;

    if (product && product.seller) {
      const expectedPath = `/${product.seller.username}/${product.slug}`;

      if (expectedPath !== location.pathname) {
        router.replace({
          ...location,
          pathname: expectedPath,
        });
      }
    }
  }

  render() {
    const { viewer, location, intl } = this.props;

    const disablePaging = location && location.query && location.query.hasOwnProperty('disablePaging');

    const locationState = location.state || { from: null, listIndex: null };

    if (viewer.product === null) {
      return <NotFound viewer={viewer} />;
    }

    const { product, currentUser } = viewer;

    if (!isProductViewable(product, true)) {
      return <NotFound viewer={viewer} />;
    }

    const { videos } = product;

    const pictures = product.pictures.map(picture =>
      ({ ...picture,
        video: (videos && videos.length && picture.url === videos[0].thumbnail.url) ? videos[0] : null
      }));

    return (
      <Application viewer={viewer}>
        <ScrollTop />
        <ProductDetailHead product={product} />

        <DeeplinkConfig
          path={`product/${product.publicId}`}
          referer={product.seller.username}
          canonicalUrl={`https://www.depop.com/product/${product.slug}`}
        />

        <div
          {...media('(min-width: 901px)', {
            display: 'flex',
            flexDirection: 'row',
          })}>

          <div
            {...css({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              width: '100%',
              '@media (min-width: 901px)': { display: 'none' },
            })}>
            <UserSummary
              user={product.seller}
              currentUser={currentUser}
              compact
            />
            <div
              {...css({
                lineHeight: '1.5',
                textAlign: 'right',
                fontSize: 12,
              })}>
              <p>{intl.formatRelative(product.dateUpdated)}</p>
              <p
                {...css({
                  maxWidth: '10em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                })}>
                {product.approximateAddress}
              </p>
            </div>
          </div>

          <div
            {...media('(min-width: 901px)', {
              width: 'calc(50% - 25px)',
              flexShrink: 0,
              marginRight: 50,
            })}>
            <Carousel>
              {pictures.map(picture =>
                <div key={picture.url}>
                  {picture.video ?
                    <VideoPlayer
                      videoURL={picture.video.url}
                      thumbnailURL={picture.url}
                      fluid={true}
                    /> :
                    <Picture
                      useLoader
                      key={picture.url}
                      picture={picture}
                      description={product.description}
                    />
                  }
                </div>
              )}
            </Carousel>
          </div>

          <div
            {...css({
              marginTop: 32,
              '@media (min-width: 901px)': {
                marginTop: 0,
                width: 'calc(50% - 25px)',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
              },
            })}>
            <div {...media('(max-width: 900px)', { display: 'none' })}>
              <UserSummary user={product.seller} currentUser={currentUser} />
            </div>
            <p
              {...css({
                margin: '16px 0 0',
                fontSize: 16,
                lineHeight: '1.2',
                maxWidth: '40em',
              })}>
              <UserText tokens={product.descriptionTokens} withBreaks={true} />
            </p>
            <p
              {...css({
                fontSize: '14px',
                marginTop: '1em',
                lineHeight: '1.2',
              })}
              {...media('(max-width: 900px)', { display: 'none' })}>
              {intl.formatRelative(product.dateUpdated)}<br />
              {product.approximateAddress}
            </p>
            <div {...css({ height: 16 })} />
            <div>
              <LikeButton
                product={product}
                currentUser={currentUser}
                from={locationState.from}
                listIndex={locationState.listIndex}
              />
              <span {...css({ marginLeft: '10px' })}>
                <FormattedMessage
                  id="ProductDetail.numLikes"
                  description="Number of users who've liked this product"
                  defaultMessage={`{count, plural,
                      =0 {0 likes}
                      one {1 like}
                      other {{count} likes}
                    }`}
                  values={{
                    count: product.counters.numLikes,
                  }}
                />
              </span>
            </div>
            <div {...css({ height: 24 })} />

            <ProductComments productRelations={product.relations} disablePaging={disablePaging} />
            <div {...css({ flexGrow: 1, height: 24 })} />
            <div {...css({ fontSize: 20, fontFamily: FAMILY_STANDARD_BOLD })}>
              {formatProductPrice(
                product.price.display,
                product.price.currencySymbol,
              )}
            </div>
            <div {...css({ height: 16 })} />
            <div>
              <DeeplinkBuyButton
                product={product}
                from={locationState.from}
                listIndex={locationState.listIndex}
              />
            </div>
          </div>
        </div>
        <div {...css({ height: 24 })} />
        <SimilarProducts productRelations={product.relations} />
      </Application>
    );
  }
}

export default Relay.createContainer(injectIntl(withRouter(ProductDetail)), {
  initialVariables: {
    id: null,
    slug: null,
  },
  fragments: {
    viewer: () => Relay.QL`
    fragment on Viewer {
        ${Application.getFragment('viewer')}
        ${NotFound.getFragment('viewer')}
        currentUser {
          ${UserSummary.getFragment('currentUser')}
          ${LikeButton.getFragment('currentUser')}
        }
        product(
          id: $id
          slug: $slug
        ) {
          id
          ${ProductDetailHead.getFragment('product')}
          ${BuyButton.getFragment('product')}
          ${LikeButton.getFragment('product')}
          publicId
          slug
          saleStatus
          pictures {
            url
            ${Picture.getFragment('picture')}
            formats {
              width
              height
              url
            }
          }
          videos {
            url
            thumbnail {
              url
            }
          }
          description
          descriptionTokens {
            ${UserText.getFragment('tokens')}
          }
          dateUpdated
          approximateAddress
          price {
            display
            currency
            currencySymbol
          }
          seller {
            ${UserSummary.getFragment('user')}
            username
            firstName
            lastName
            picture {
              formats {
                width
                height
                url
              }
            }
          }
          counters {
            numLikes
          }
          relations {
            ${SimilarProducts.getFragment('productRelations')}
            ${ProductComments.getFragment('productRelations')}
          }
        }
      }
    `,
  },
});



// WEBPACK FOOTER //
// ../src/client/apps/shops/views/ProductDetail.js